import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, forwardRef, Inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UIRouter }         from '@uirouter/core';

import { ApiService }            from '../../../api/api.service';
import { FileUploadOptionsModel } from './models/file-upload.options';
import { MessagesService }       from '../../../messages/messages.service';
import { S3OptionsModel }        from './models/s3-options';
import { S3UploadService }       from './s3-upload.service';
import { SessionService }        from '../../../sessions/session.service';

@Component({
  selector: 'wor-file-upload',
  template: require('./file-upload.component.html')
})
export class FileUploadComponent implements OnInit, OnDestroy {
  @Input() options: FileUploadOptionsModel;

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  acceptedTypes: string;
  file: File;
  preview: string;
  reader: FileReader;
  s3BucketLink: string;
  uploadReady = false;
  uploading = false;
  dirty = false;

  private imageExtensions = ['gif', 'jpeg', 'jpg', 'png'];

  constructor (
    @Inject(forwardRef(() => ApiService)) private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    @Inject(forwardRef(() => MessagesService)) private messagesService: MessagesService,
    @Inject(forwardRef(() => S3UploadService)) private s3UploadService: S3UploadService,
    @Inject(forwardRef(() => SessionService))
    private sessionService: SessionService,
    private translateService: TranslateService,
    private uiRouter: UIRouter
  ) {}

  ngOnInit(): void {
    this.preview       = this.options?.file;
    this.acceptedTypes = this.getAcceptedTypes();
  }

  ngOnDestroy(): void {
    if (this.reader) {
      this.reader.removeEventListener('load', this.handleReaderLoad);
    }
  }

  getAcceptedTypes(): string {
    switch (this.options?.accept) {
      case 'images':
      default:
        return '.gif,.jpeg,.jpg,.png';
    }
  }

  private getExtension(filename: string): string[] {
    return (/[.]/.exec(filename)) ? /[^.]+$/.exec(filename) : ['png'];
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file  = input.files?.[0];

    if (!file) return;

    if (file.size > this.options.size) {
      input.value = null;
      this.messagesService.show(`${this.options.type}'s cannot exceed ${this.prettifySize()}`, 'error');
      return;
    }

    if (this.options.accept === 'images' && this.imageExtensions.indexOf(this.getExtension(file.name)[0]) < 0) {
      input.value = null;
      const types = this.imageExtensions.map(e => e.toUpperCase()).join(', ');
      this.messagesService.show(`File must be one of the following types: ${types}`, 'error');
      return;
    }

    this.file        = file;
    this.uploadReady = true;

    this.reader = new FileReader();
    this.reader.addEventListener('load', this.handleReaderLoad);
    this.reader.readAsDataURL(file);
  }

  private handleReaderLoad = (e: any): void => {
    this.dirty   = true;
    this.preview = e.target.result;
    this.cdr.detectChanges();
  };

  isAdvertisement(): boolean {
    return this.options?.type === 'advertisement';
  }

  prettifySize(): string {
    const mb = this.options.size / 1000000;
    if (mb < 1) {
      return `${(mb * 1000).toFixed(0)}KB`;
    }
    return `${mb.toFixed(0)}MB`;
  }

  showPreview(): boolean {
    return !!this.preview && !this.options?.hidePreview;
  }

  upload(): void {
    this.uploading = true;

    this.apiService.get('/company/s3_bucket_link')
      .then(data => {
        this.s3BucketLink = data;

        return this.isAdvertisement()
          ? this.apiService.get('/workshop_admin/s3_signature_for_ad_v4')
          : this.apiService.get('/company/s3_signature_v4', [this.options.type, this.getExtension(this.file.name)]);
      })
      .then((options: S3OptionsModel) => {
        const call = this.s3UploadService.upload(this.file, options);

        const onLoad = (e) => {
          if (e.target.status === 204) {
            Promise.resolve()
              .then(() => this.isAdvertisement()
                ? Promise.resolve(true)
                : this.apiService.post('/company/s3_path', {
                    file_type: this.options.type,
                    s3_link  : options.key
                  })
              )
              .then(() => this.sessionService.refresh())
              .then(() => {
                this.uploading = false;
                this.dirty     = false;

                this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.UPLOAD_SUCCESSFUL'), 'success');
                this.uiRouter.stateService.reload();
              })
              .catch(err => {
                this.uploading = false;
                this.messagesService.error(err);
              })
              .finally(() => {
                call.request.removeEventListener('load', onLoad);
              });
          }
        };

        const onError = (e) => {
          this.uploading = false;
          this.messagesService.show(e.target.responseText, 'error');
          call.request.removeEventListener('error', onError);
        };

        call.request.addEventListener('load', onLoad);
        call.request.addEventListener('error', onError);
        call.request.open('POST', this.s3BucketLink, true);
        call.request.send(call.fd);
      })
      .catch(err => {
        this.uploading = false;
        this.messagesService.error(err);
      });
  }
}
